import React, { useState } from 'react';
import { AlertTriangle, X } from 'lucide-react';
import { Button } from '../../../../components/ui/button';
import { cn } from '../../../../lib/utils';

/**
 * DSR Alert Banners Component
 * Shows amber auto-dismissing alerts for:
 * 1. Missing deadline - any open request with no deadline set
 * 2. 3rd party unverified - open 3rd-party request without written authorisation
 */
export function DSRAlertBanners({ dsrs = [] }) {
  const [dismissedAlerts, setDismissedAlerts] = useState(new Set());

  // Calculate missing deadline count
  const missingDeadlineCount = dsrs.filter(dsr => {
    const isOpen = dsr.status && !['responded', 'closed', 'withdrawn', 'refused'].includes(dsr.status);
    const hasNoDeadline = !dsr.deadline; // Check API's direct deadline field
    return isOpen && hasNoDeadline;
  }).length;

  // Find first unverified 3rd party request
  const unverifiedThirdParty = dsrs.find(dsr => {
    const isOpen = dsr.status && !['responded', 'closed', 'withdrawn', 'refused'].includes(dsr.status);
    const isThirdParty = dsr.requester?.is_third_party || dsr.requester?.is_agent;
    const notAuthorized = !dsr.requester?.agent_authorization && !dsr.requester?.third_party_authorization;
    return isOpen && isThirdParty && notAuthorized;
  });

  const dismissAlert = (alertKey) => {
    setDismissedAlerts(prev => {
      const updated = new Set(prev);
      updated.add(alertKey);
      return updated;
    });

    // Auto-dismiss after alert condition is resolved in next refresh
    // For now, they stay dismissed until page refresh
  };

  const alerts = [];

  // Alert 1: Missing deadline
  if (missingDeadlineCount > 0 && !dismissedAlerts.has('missing-deadline')) {
    alerts.push({
      key: 'missing-deadline',
      type: 'warning',
      icon: AlertTriangle,
      message: `${missingDeadlineCount} request(s) have no deadline set. Assign one to avoid breach of statutory timelines.`
    });
  }

  // Alert 2: 3rd party unverified
  if (unverifiedThirdParty && !dismissedAlerts.has('third-party-unverified')) {
    alerts.push({
      key: 'third-party-unverified',
      type: 'warning',
      icon: AlertTriangle,
      message: `${unverifiedThirdParty.dsr_reference || 'Request'} is a third-party request. Written authorisation from the data subject has not been confirmed.`
    });
  }

  if (alerts.length === 0) return null;

  return (
    <div className="space-y-3">
      {alerts.map(alert => {
        const Icon = alert.icon;
        return (
          <div
            key={alert.key}
            className={cn(
              'flex items-start gap-3 p-4 rounded-lg border',
              alert.type === 'warning' &&
                'bg-amber-50 dark:bg-amber-950/30 border-amber-200 dark:border-amber-800/50'
            )}
          >
            <Icon className="h-5 w-5 text-amber-600 dark:text-amber-400 mt-0.5 flex-shrink-0" />
            <div className="flex-1">
              <p className="text-sm text-amber-800 dark:text-amber-200">
                {alert.message}
              </p>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => dismissAlert(alert.key)}
              className="text-amber-600 dark:text-amber-400 hover:bg-amber-100 dark:hover:bg-amber-900/40 flex-shrink-0"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        );
      })}
    </div>
  );
}

export default DSRAlertBanners;
